import React, { ReactNode } from 'react';
import { Box, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import EventBusyOutlinedIcon from '@mui/icons-material/EventBusyOutlined';
import ScheduleOutlinedIcon from '@mui/icons-material/ScheduleOutlined';
import { DeactivateIcon, TrashIcon } from '@shared/custom-icons';
import dayjs from 'dayjs';
import { getLLDate } from '@retail/utils';
import { useAppTFunction } from '@retail/app/i18n';

interface Props {
  validFrom?: Date;
  validTo?: Date;
  showFutureIndicator?: boolean;
  tooltipHeader?: ReactNode;
  onDeactivate?: () => void;
  onDelete?: () => void;
  children: ReactNode;
}

export const ConditionCell = ({
  validFrom,
  validTo,
  showFutureIndicator,
  tooltipHeader,
  onDeactivate,
  onDelete,
  children,
}: Props) => {
  const t = useAppTFunction();
  const isFuture = validFrom ? dayjs(validFrom).isAfter(dayjs(), 'day') : false;

  return (
    <Box sx={{ position: 'relative', width: '100%', height: '100%' }}>
      <Tooltip
        title={
          <Stack gap={1}>
            {tooltipHeader}
            <Stack>
              <Typography variant="body2">
                {t('calculus.activeFrom')}: {getLLDate(validFrom)}
              </Typography>
              {validTo && (
                <Typography variant="body2">
                  {t('calculus.activeTo')}: {getLLDate(validTo)}
                </Typography>
              )}
            </Stack>
          </Stack>
        }
      >
        <Stack direction="row" alignItems="center" gap={0.5}>
          {children}
          {validTo && (
            <EventBusyOutlinedIcon
              color="error"
              titleAccess={t('calculus.activeTo')}
              sx={{ width: 18, height: 18 }}
            />
          )}
          {showFutureIndicator && isFuture && (
            <ScheduleOutlinedIcon
              color="action"
              titleAccess={t('myAssortment.filter.future')}
              sx={{ width: 18, height: 18 }}
            />
          )}
        </Stack>
      </Tooltip>
      {(onDeactivate || onDelete) && (
        <Stack direction="row" sx={{ position: 'absolute', top: 0, right: 0 }}>
          {onDeactivate && (
            <Tooltip title={t('calculus.actions.deactivate')}>
              <IconButton
                onClick={onDeactivate}
                aria-label={t('calculus.deactivate')}
                size="small"
                sx={{ p: 0.25 }}
              >
                <DeactivateIcon sx={{ width: 14, height: 14 }} />
              </IconButton>
            </Tooltip>
          )}
          {onDelete && (
            <Tooltip title={t('calculus.actions.deleteFromDraft')}>
              <IconButton
                onClick={onDelete}
                aria-label={t('calculus.actions.deleteFromDraft')}
                size="small"
                sx={{ p: 0.25 }}
              >
                <TrashIcon sx={{ width: 14, height: 14 }} />
              </IconButton>
            </Tooltip>
          )}
        </Stack>
      )}
    </Box>
  );
};
